import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { packingApi } from './packing.api';
import { boxKeys } from './packing.queries';
import type {
  CreateBoxRequest,
  UpdateBoxRequest,
  UpdateBoxStatusRequest,
  AddRegistrationToBoxRequest,
  BoxesListParams
} from '../types';

export function useCreateBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: (data: CreateBoxRequest) => packingApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boxKeys.metrics() });
    }
  });
}

export function useUpdateBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBoxRequest }) =>
      packingApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boxKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: boxKeys.detail(variables.id) });
    }
  });
}

export function useDeleteBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: (id: number) => packingApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boxKeys.metrics() });
    }
  });
}

export function useBulkDeleteBoxesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<BoxesListParams> }
    ) => packingApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: boxKeys.metrics() });
    }
  });
}

export function useAddRegistrationToBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: ({
      boxId,
      data
    }: {
      boxId: number;
      data: AddRegistrationToBoxRequest;
    }) => packingApi.addRegistration(boxId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('registrationAdded')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.all });
    }
  });
}

export function useRemoveRegistrationFromBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: ({
      boxId,
      registrationId
    }: {
      boxId: number;
      registrationId: number;
    }) => packingApi.removeRegistration(boxId, registrationId),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('registrationRemoved')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.all });
    }
  });
}

export function useUpdateBoxStatusMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBoxStatusRequest }) =>
      packingApi.updateStatus(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('statusUpdated')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.all });
    }
  });
}

export function useOpenBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: (id: number) => packingApi.open(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('openSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.all });
    }
  });
}

export function useCloseBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: (id: number) => packingApi.close(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('closeSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.all });
    }
  });
}

export function useRollbackBoxMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('packing');

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      packingApi.rollback(id, { reason }),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('rollbackSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boxKeys.all });
    }
  });
}
